import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { auth } from "../../../firebaseConfig";
import { endpoints } from "../../../hooks/endpoints";
import { getToken, isPhoneValid } from "../../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
    invokePortalRefetch,
    invokeUserRefetch,
    userData as userDataDispatch,
} from "../../../store/slices/storeSlice";
import UpdatePortalImages from "./UpdatePortalImages";

const EditPortal = () => {
  const [userData, setUserData] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [portalUpdateResponse, setPortalUpdateResponse] = useState(null);

  const { userProfileData, portal } = useAppSelector((state) => state.store);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("user");
    dispatch(userDataDispatch(null));
    signOut(auth);
    navigate("/");
  };

  const handleUpdatePortal = async (e) => {
    e.preventDefault();
    try {
      const checkWhatsapp = isPhoneValid(userData.whatsapp);
      const el = document.getElementById("teacher-whatsapp") as any;
      if (!checkWhatsapp.isValid) {
        if (el) el.style = "border: 1px solid red";
        return toast.error(
          <span className="text-red-600">{checkWhatsapp.message}</span>
        );
      } else {
        el.style = "";
      }

      setIsPending(true);
      const data = await fetch(endpoints.updatePortal, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "POST",
        body: JSON.stringify({
          profile_pic: userData.profile_pic.split("&image_version=")[0],
          logo: userData.logo.split("&image_version=")[0],
          whatsapp: userData.whatsapp.trim(),
          name: userData.name.trim(),
        }),
      }).then((response) => response.json());

      toast.success(<span className="text-green-700">{data?.message}</span>);
      dispatch(invokePortalRefetch());
      dispatch(invokeUserRefetch());
    } catch (e) {
      toast.error(<span className="text-red-600">حدث خطأ</span>);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    if (userProfileData && portal) {
      setUserData({
        profile_pic: portal?.profile_pic,
        logo: portal?.logoUrl,
        name: userProfileData?.teacher_name,
        phone: userProfileData?.user_login,
        email: userProfileData?.user_email,
        domain: userProfileData?.portal,
        subject: userProfileData?.teacher_subject,
        whatsapp: portal?.whatsapp_number,
      });
    }
  }, [userProfileData, portal]);

  return (
    <>
      <form
        onSubmit={handleUpdatePortal}
        className="rounded-lg p-4 bg-white border shadow-sm space-y-6"
      >
        <h4 className="mb-4 pb-1 border-b-2 border-primary w-fit text-lg font-bold">
          تعديل بيانات المنصة
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label htmlFor="teacher-name" className="mb-1 font-medium">
              إسم المدرس
            </label>
            <input
              required
              id="teacher-name"
              type="text"
              placeholder="إسم المدرس"
              value={userData?.name}
              onChange={(e) =>
                setUserData({ ...userData, name: e.target.value })
              }
              className="border border-gray-300 rounded-lg px-3 py-2 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="teacher-whatsapp" className="mb-1 font-medium">
              رقم الواتساب
            </label>
            <input
              required
              id="teacher-whatsapp"
              type="text"
              placeholder="01XXXXXXXXX"
              value={userData?.whatsapp}
              onChange={(e) =>
                setUserData({ ...userData, whatsapp: e.target.value })
              }
              className="border border-gray-300 rounded-lg px-3 py-2 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label htmlFor="teacher-domain" className="mb-1 font-medium">
              رابط المنصة
            </label>
            <input
              id="teacher-domain"
              type="text"
              placeholder="رابط المنصة"
              value={userData?.domain}
              disabled
              className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="teacher-phone" className="mb-1 font-medium">
              رقم الهاتف
            </label>
            <input
              id="teacher-phone"
              type="text"
              placeholder="رقم الهاتف"
              value={userData?.phone}
              disabled
              className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label htmlFor="teacher-mail" className="mb-1 font-medium">
              الإيميل
            </label>
            <input
              id="teacher-mail"
              type="text"
              placeholder="الإيميل"
              value={userData?.email}
              disabled
              className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="teacher-subject" className="mb-1 font-medium">
              المادة
            </label>
            <input
              id="teacher-subject"
              type="text"
              placeholder="المادة"
              value={userData?.subject}
              disabled
              className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <div className=" text-center px-2 md:mb-0">
            <h5 className="mb-2 font-semibold">صورة شخصية</h5>
            <UpdatePortalImages
              userData={userData}
              setUserData={setUserData}
              imgType="profile_pic"
            />
          </div>
          <div className=" text-center px-2">
            <h5 className="mb-2 font-semibold">لوجو المنصة</h5>
            <UpdatePortalImages
              userData={userData}
              setUserData={setUserData}
              imgType="logo"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <Button className="bg-primary hover:bg-secondary text-white px-6 py-2 rounded-lg cursor-pointer w-full md:w-1/4">
            {isPending ? <Spinner variant="button" /> : "حفظ التعديلات"}
          </Button>

          <Button
            type="button"
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg cursor-pointer w-full md:w-1/4"
          >
            تسجيل الخروج
          </Button>
        </div>
      </form>
    </>
  );

  return (
    <>
      <form
        className="my-2 mb-3 rounded-ea p-3 bg-white"
        onSubmit={handleUpdatePortal}
        style={{ border: "1px solid #ddd" }}
      >
        <h4
          className="p-2 mb-4"
          style={{
            width: "fit-content",
            borderBottom: "2px solid #673de6",
          }}
        >
          تعديل بيانات المنصة
        </h4>
        <div className="portal-dashboard-images">
          <div className="col-md-6 col-12 text-center px-2">
            <h5>صورة شخصية</h5>
            <UpdatePortalImages
              userData={userData}
              setUserData={setUserData}
              imgType="profile_pic"
            />
          </div>
          <div className="col-md-6 col-12 text-center px-2">
            <h5>لوجو المنصة</h5>
            <UpdatePortalImages
              userData={userData}
              setUserData={setUserData}
              imgType="logo"
            />
          </div>
        </div>

        <div className="portal-dashboard-form">
          <div className="form-floating mb-1">
            <input
              required
              type="text"
              className="form-control rounded-ea"
              id="teacher-name"
              placeholder="إسم المدرس"
              value={userData?.name}
              onChange={(e) => {
                setUserData({
                  ...userData,
                  name: e.target.value,
                });
              }}
            />
            <label htmlFor="teacher-name">إسم المدرس</label>
          </div>
          <div className="form-floating mb-1">
            <input
              required
              type="text"
              className="form-control rounded-ea"
              id="teacher-whatsapp"
              placeholder="01XXXXXXXXX"
              value={userData?.whatsapp}
              onChange={(e) => {
                setUserData({
                  ...userData,
                  whatsapp: e.target.value,
                });
              }}
            />
            <label htmlFor="teacher-name">رقم الواتساب</label>
          </div>
        </div>

        <div className="portal-dashboard-form">
          <div className="form-floating mb-1">
            <input
              type="text"
              className="form-control rounded-ea"
              id="teacher-domain"
              placeholder="name@example.com"
              disabled
              value={userData?.domain}
            />
            <label htmlFor="teacher-phone">رابط المنصة</label>
          </div>
          <div className="form-floating mb-1">
            <input
              type="text"
              className="form-control rounded-ea"
              id="teacher-phone"
              placeholder="name@example.com"
              disabled
              value={userData?.phone}
            />
            <label htmlFor="teacher-phone">رقم الهاتف</label>
          </div>
        </div>

        <div className="portal-dashboard-form">
          <div className="form-floating mb-1">
            <input
              type="text"
              className="form-control rounded-ea"
              id="teacher-mail"
              placeholder="name@example.com"
              value={userData?.email}
              disabled
            />
            <label htmlFor="teacher-mail">الإيميل</label>
          </div>
          <div className="form-floating mb-1">
            <input
              type="text"
              className="form-control rounded-ea"
              id="teacher-subject"
              placeholder="name@example.com"
              value={userData?.subject}
              disabled
            />
            <label htmlFor="teacher-subject">المادة</label>
          </div>
        </div>

        <div className="d-flex align-items-center gap-3">
          {!isPending && (
            <input
              type="submit"
              className="rounded-ea my-2 w-25 mw-100 mx-auto"
              value="حفظ التعديلات"
            />
          )}
          {isPending && (
            <button
              className="rounded-ea my-2 w-25 mw-100 mx-auto"
              type="button"
              disabled
            >
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              حفظ التعديلات...
            </button>
          )}
        </div>
        {!!portalUpdateResponse?.message && (
          <div
            className={`alert alert-${
              portalUpdateResponse?.status === 200 ? "success" : "danger"
            } rounded-ea w-100 text-center`}
            role="alert"
          >
            {portalUpdateResponse?.message}
          </div>
        )}
        <div className="p-0 m-0 d-flex">
          <button
            type="button"
            onClick={handleLogout}
            className="btn btn-danger text-white rounded-ea my-2 w-25 mw-100 mx-auto"
          >
            تسجيل الخروج
          </button>
        </div>
      </form>
    </>
  );
};

export default EditPortal;
